import { Card as ShadCard, CardContent as ShadCardContent, CardDescription as ShadCardDescription, CardFooter as ShadCardFooter, CardTitle as ShadCardTitle } from '@/components/ui/Card';
import { cn } from '@/utils';
import * as React from 'react';

const Card = React.forwardRef(({ className, ...props }, ref) => (
    <ShadCard
        className={cn('tw-flex tw-flex-col tw-h-full tw-border tw-border-solid tw-border-border tw-rounded-lg', className)}
        ref={ref}
        {...props}
    />
));
Card.displayName = 'Card';

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
    <ShadCardDescription
        className={cn('!tw-p-2 tw-flex tw-justify-between tw-items-center', className)}
        ref={ref}
        {...props}
    />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef(({ className, ...props }, ref) => (
    <ShadCardContent
        className={cn('!tw-p-2', className)}
        ref={ref}
        {...props}
    />
));
CardContent.displayName = 'CardHeader';

const CardHeader = React.forwardRef(
    ({ className, title, subtitle, button, ...props }, ref) => (
        <div
            className={cn(
                'tw-p-2 tw-bg-header-custom tw-text-primary tw-rounded-t-xl tw-flex tw-items-center tw-justify-between tw-space-y-2 sm:tw-space-y-0 sm:tw-space-x-4',
                className
            )}
            ref={ref}
            {...props}
        >
            {/* Title and Subtitle */}
            <div className="tw-flex tw-flex-col tw-justify-center">
                <span className="tw-text-lg tw-font-bold">{title}</span>
                {subtitle && (
                    <span className="tw-text-sm tw-text-secondary">{subtitle}</span>
                )}
            </div>

            {/* Button */}
            <div className="tw-flex tw-items-center">{button}</div>
        </div>
    )
);
CardHeader.displayName = 'CardHeader';

const CardHeaderImage = React.forwardRef(({ className, size = '', ...props }, ref) => (
    <div
        className={cn('tw-overflow-hidden tw-rounded-t-lg tw-bg-cover tw-w-full', className)}
        ref={ref}
        style={{ width: size, margin: '0' }}
        {...props}
    />
));
CardHeaderImage.displayName = 'CardHeaderImage';

const CardText = React.forwardRef(({ className, title = '', children, ...props }, ref) => (
    <div
        className={cn('tw-mb-4', className)}
        ref={ref}
        {...props}
    >
        <h6 className="">{title}</h6>
        <p className="tw-text-justify">{children}</p>
    </div>
));
CardHeaderImage.displayName = 'CardText';

const CardStat = React.forwardRef(({ className, ...props }, ref) => (
    <div
        className={cn('tw-p-4 tw-text-2xl tw-font-bold tw-text-gray-800', className)}
        ref={ref}
        {...props}
    />
));
CardStat.displayName = 'CardText';

const CardFooter = React.forwardRef(({ className, children, ...props }, ref) => (
    <ShadCardFooter
        className={cn('tw-grow !tw-p-2', className)}
        ref={ref}
        {...props}
    >
        <div className="tw-mt-auto tw-w-full">
            {children}
        </div>
    </ShadCardFooter>
));
CardHeader.displayName = 'CardHeader';

const CardWrapper = ({ cardSize = 'default', className, children, ...props }) => {
    const childCount = React.Children.count(children);

    const sizeClasses = {
        small: `tw-grid tw-grid-cols-2 md:tw-grid-cols-${Math.min(childCount, 3)} lg:tw-grid-cols-${Math.min(childCount, 4)} xl:tw-grid-cols-${Math.min(childCount, 6)} tw-gap-2`,
        default: `tw-grid tw-grid-cols-1 md:tw-grid-cols-${Math.min(childCount, 2)} lg:tw-grid-cols-${Math.min(childCount, 3)} xl:tw-grid-cols-${Math.min(childCount, 4)} tw-gap-4`,
        large: `tw-grid tw-grid-cols-1 md:tw-grid-cols-${Math.min(childCount, 1)} lg:tw-grid-cols-${Math.min(childCount, 2)} xl:tw-grid-cols-${Math.min(childCount, 3)} tw-gap-6`
    };

    return (
        <div className={cn(sizeClasses[cardSize], className)} {...props}>
            {children}
        </div>
    );
};

const CardTitle = ShadCardTitle;

export {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardHeaderImage,
    CardStat,
    CardText,
    CardTitle,
    CardWrapper
};
